import { morph } from '@theme/morph';
import { Component } from '@theme/component';
import { CartUpdateEvent, ThemeEvents } from '@theme/events';
import { DialogComponent, DialogCloseEvent } from '@theme/dialog';
import { mediaQueryLarge, isMobileBreakpoint, getIOSVersion } from '@theme/utilities';

export class QuickAddComponent extends Component {
  /** @type {AbortController | null} */
  #abortController = null;
  /** @type {Map<string, Element>} */
  #cachedContent = new Map();
  /** @type {Map<string, Element>} */
  #cachedDescriptionSummary = new Map();
  /** @type {Map<string, string>} */
  #cachedColorScheme = new Map();

  get productPageUrl() {
    const fromDataset = this.dataset.productUrl;
    if (fromDataset) {
      const url = new URL(fromDataset, window.location.origin);
      const variantId = this.dataset.selectedVariantId;
      if (variantId && !url.searchParams.has('variant')) {
        url.searchParams.set('variant', variantId);
      }
      return url.toString();
    }

    const productCard = /** @type {import('./product-card').ProductCard | null} */ (this.closest('product-card'));
    const productLink = productCard?.getProductCardLink();

    if (!productLink?.href) return '';

    const url = new URL(productLink.href);

    if (url.searchParams.has('variant')) {
      return url.toString();
    }

    const selectedVariantId = this.#getSelectedVariantId();
    if (selectedVariantId) {
      url.searchParams.set('variant', selectedVariantId);
    }

    return url.toString();
  }

  /**
   * Gets the currently selected variant ID from the product card or dataset override
   * @returns {string | null} The variant ID or null
   */
  #getSelectedVariantId() {
    if (this.dataset.selectedVariantId) return this.dataset.selectedVariantId;

    const productCard = /** @type {import('./product-card').ProductCard | null} */ (this.closest('product-card'));
    return productCard?.getSelectedVariantId() || null;
  }

  /**
   * Open quick-add modal for an arbitrary product (e.g. VAP toebehoren).
   * @param {{ url: string, variantId?: string, title?: string, description?: string }} options
   */
  openForProduct = async ({ url, variantId, title, description }) => {
    this.#loadGeneration += 1;
    this.dataset.productUrl = url;
    this.dataset.selectedVariantId = variantId || '';
    this.dataset.quickAddSource = 'vap';
    if (title) this.dataset.productTitle = title;
    if (description) this.dataset.productDescription = description;
    else delete this.dataset.productDescription;
    await this.handleClick(new Event('click'));
  };

  connectedCallback() {
    super.connectedCallback();

    mediaQueryLarge.addEventListener('change', this.#closeQuickAddModal);
  }

  disconnectedCallback() {
    super.disconnectedCallback();

    mediaQueryLarge.removeEventListener('change', this.#closeQuickAddModal);
    this.#abortController?.abort();
  }

  /**
   * Handles quick add button click
   * @param {Event} event - The click event
   */
  handleClick = async (event) => {
    event.preventDefault();

    const currentUrl = this.productPageUrl;
    if (!currentUrl) return;

    const cached = this.#cachedContent.get(currentUrl);
    if (cached) {
      const freshContent = /** @type {Element} */ (cached.cloneNode(true));
      await this.updateQuickAddModal(freshContent);
      this.#openQuickAddModal();
      return;
    }

    const loadId = ++this.#loadGeneration;
    this.#showLoadingPlaceholder();
    this.#openQuickAddModal();

    let productGrid;
    try {
      const html = await this.fetchProductPage(currentUrl);
      if (loadId !== this.#loadGeneration) return;

      if (html) {
        const summaryElement = this.#extractDescriptionSummary(html);
        if (summaryElement) {
          this.#cachedDescriptionSummary.set(currentUrl, summaryElement);
        }

        const colorSchemeClass = this.#extractProductColorScheme(html);
        if (colorSchemeClass) {
          this.#cachedColorScheme.set(currentUrl, colorSchemeClass);
        }

        const gridElement = html.querySelector('[data-product-grid-content]');
        if (gridElement) {
          productGrid = /** @type {Element} */ (gridElement.cloneNode(true));
          this.#cachedContent.set(currentUrl, productGrid);
        }
      }
    } catch (error) {
      if (loadId !== this.#loadGeneration) return;
      console.error('Quick add — productpagina laden mislukt:', error);
    }

    if (loadId !== this.#loadGeneration) return;

    if (productGrid) {
      const freshContent = /** @type {Element} */ (productGrid.cloneNode(true));
      await this.updateQuickAddModal(freshContent);
    } else {
      this.#showLoadingError();
    }
  };

  /** @type {number} */
  #loadGeneration = 0;

  #showLoadingPlaceholder() {
    const modalContent = document.getElementById('quick-add-modal-content');
    if (!(modalContent instanceof HTMLElement)) return;

    const title = (this.dataset.productTitle || '').trim();
    modalContent.replaceChildren();
    modalContent.classList.remove('quick-add-modal__content--has-desc');
    modalContent.classList.add('quick-add-modal__content--loading');
    modalContent.setAttribute('aria-busy', 'true');

    const loading = document.createElement('div');
    loading.className = 'quick-add-modal__loading';
    loading.innerHTML = `
      <div class="quick-add-modal__loading-media" aria-hidden="true"></div>
      <div class="quick-add-modal__loading-details">
        <div class="quick-add-modal__loading-title">${title ? this.#escapeHtml(title) : '&nbsp;'}</div>
        <div class="quick-add-modal__loading-line quick-add-modal__loading-line--short" aria-hidden="true"></div>
        <div class="quick-add-modal__loading-line" aria-hidden="true"></div>
        <div class="quick-add-modal__loading-line" aria-hidden="true"></div>
        <div class="quick-add-modal__loading-desc" aria-hidden="true"></div>
        <div class="quick-add-modal__loading-desc quick-add-modal__loading-desc--short" aria-hidden="true"></div>
        <div class="quick-add-modal__loading-actions" aria-hidden="true"></div>
      </div>
    `;
    modalContent.appendChild(loading);
  }

  #showLoadingError() {
    const modalContent = document.getElementById('quick-add-modal-content');
    if (!(modalContent instanceof HTMLElement)) return;

    modalContent.classList.remove('quick-add-modal__content--loading');
    modalContent.removeAttribute('aria-busy');
    modalContent.replaceChildren();

    const error = document.createElement('p');
    error.className = 'quick-add-modal__error';
    error.textContent = 'Product kon niet worden geladen. Probeer het opnieuw.';
    modalContent.appendChild(error);
  }

  /** @param {string} value */
  #escapeHtml(value) {
    return value
      .replace(/&/g, '&amp;')
      .replace(/</g, '&lt;')
      .replace(/>/g, '&gt;')
      .replace(/"/g, '&quot;');
  }

  /** @param {QuickAddDialog} dialogComponent */
  #stayVisibleUntilDialogCloses(dialogComponent) {
    this.toggleAttribute('stay-visible', true);

    dialogComponent.addEventListener(DialogCloseEvent.eventName, () => this.toggleAttribute('stay-visible', false), {
      once: true,
    });
  }

  /**
   * @param {string} [url]
   */
  invalidateCacheForUrl(url) {
    this.#invalidateCache(url);
  }

  #openQuickAddModal = () => {
    const dialogComponent = document.getElementById('quick-add-dialog');
    if (!(dialogComponent instanceof QuickAddDialog)) return;

    this.#stayVisibleUntilDialogCloses(dialogComponent);

    dialogComponent.showDialog();
  };

  #closeQuickAddModal = () => {
    const dialogComponent = document.getElementById('quick-add-dialog');
    if (!(dialogComponent instanceof QuickAddDialog)) return;

    dialogComponent.closeDialog();
  };

  /**
   * Fetches the product page content
   * @param {string} productPageUrl - The URL of the product page to fetch
   * @returns {Promise<Document | null>}
   */
  async fetchProductPage(productPageUrl) {
    if (!productPageUrl) return null;

    // We use this to abort the previous fetch request if it's still pending.
    this.#abortController?.abort();
    this.#abortController = new AbortController();

    try {
      const response = await fetch(productPageUrl, {
        signal: this.#abortController.signal,
      });

      if (!response.ok) {
        throw new Error(`Failed to fetch product page: HTTP error ${response.status}`);
      }

      const responseText = await response.text();
      const html = new DOMParser().parseFromString(responseText, 'text/html');

      return html;
    } catch (error) {
      if (error.name === 'AbortError') {
        return null;
      } else {
        throw error;
      }
    } finally {
      this.#abortController = null;
    }
  }

  /**
   * Re-renders the variant picker.
   * @param {Element} productGrid - The product grid element
   */
  async updateQuickAddModal(productGrid) {
    const modalContent = document.getElementById('quick-add-modal-content');

    if (!productGrid || !modalContent) return;

    modalContent.classList.remove('quick-add-modal__content--loading');
    modalContent.removeAttribute('aria-busy');

    if (isMobileBreakpoint()) {
      const productDetails = productGrid.querySelector('.product-details');
      if (!productDetails) return;
      const productFormComponent = productGrid.querySelector('product-form-component');
      const variantPicker = productGrid.querySelector('variant-picker');
      const productPrice = productGrid.querySelector('product-price');
      const descriptionBlock =
        this.#getCachedDescriptionSummary()?.cloneNode(true) ||
        productDetails.querySelector('.text-summary') ||
        this.#findDescriptionBlock(productDetails);
      const productTitle = document.createElement('a');
      productTitle.textContent = this.dataset.productTitle || '';

      // Make product title as a link to the product page
      productTitle.href = this.productPageUrl;

      if (!productFormComponent || !productPrice || !productTitle) return;

      const productHeader = document.createElement('div');
      productHeader.classList.add('product-header');

      productHeader.appendChild(productTitle);
      productHeader.appendChild(productPrice);
      productGrid.appendChild(productHeader);
      if (descriptionBlock) productGrid.appendChild(descriptionBlock.cloneNode(true));
      if (variantPicker) productGrid.appendChild(variantPicker);
      productGrid.appendChild(productFormComponent);
      productDetails.remove();
    }

    this.#pruneQuickAddGrid(productGrid);

    morph(modalContent, productGrid);

    this.#syncVariantSelection(modalContent);
    this.#cleanupModalContent(modalContent);
    this.#ensureDescription(modalContent);
    this.#applyCommerceColorScheme(modalContent, this.#getCachedColorScheme());
    this.#resetModalProductForms(modalContent);
  }

  /**
   * @returns {string | null}
   */
  #getCachedColorScheme() {
    const url = this.productPageUrl;
    return url ? this.#cachedColorScheme.get(url) || null : null;
  }

  /**
   * PDP-kleurenschema (oranje ATC) zit op .product-information, niet in de gekloonde grid.
   * @param {Document} html
   * @returns {string | null}
   */
  #extractProductColorScheme(html) {
    const productInformation = html.querySelector('.product-information');
    if (!productInformation) return null;

    for (const className of productInformation.classList) {
      if (className.startsWith('color-')) return className;
    }

    return null;
  }

  /**
   * @param {Element} modalContent
   * @param {string | null} colorSchemeClass
   */
  #applyCommerceColorScheme(modalContent, colorSchemeClass) {
    const commerce = modalContent.querySelector('.product-form-buttons');
    if (!(commerce instanceof HTMLElement)) return;

    for (const className of [...commerce.classList]) {
      if (className.startsWith('color-')) commerce.classList.remove(className);
    }

    if (colorSchemeClass) commerce.classList.add(colorSchemeClass);
  }

  /** @param {string} [url] */
  #invalidateCache(url) {
    if (url) {
      this.#cachedContent.delete(url);
      this.#cachedDescriptionSummary.delete(url);
      this.#cachedColorScheme.delete(url);
      return;
    }

    this.#cachedContent.clear();
    this.#cachedDescriptionSummary.clear();
    this.#cachedColorScheme.clear();
  }

  /** @param {Element} modalContent */
  #resetModalProductForms(modalContent) {
    for (const el of modalContent.querySelectorAll('product-form-component')) {
      const form = /** @type {HTMLElement & { resetAddToCartState?: () => void, updatedCallback?: () => void }} */ (el);
      form.resetAddToCartState?.();
      form.updatedCallback?.();
    }
  }

  /**
   * Verwijder PDP-blokken die in quick-add niet horen + overbodige variant-picker.
   * @param {Element} productGrid
   */
  #pruneQuickAddGrid(productGrid) {
    const removeSelectors = [
      'product-labels',
      '.product-labels',
      'product-button',
      'variant-additional-products',
      'variant-teaser-text',
      'paint-calculator',
      'product-custom-property-component',
      '.product-configurator-section',
      'shopify-payment-terms',
      'shopify-accelerated-checkout',
      '.accelerated-checkout-block',
      '.shopify-payment-button',
      'pickup-availability',
      'product-reviews',
      '.view-more-details__wrapper',
    ];

    for (const selector of removeSelectors) {
      productGrid.querySelectorAll(selector).forEach((el) => el.remove());
    }

    const buyButtons = productGrid.querySelector('.buy-buttons-block');
    if (buyButtons?.parentElement) {
      let sibling = buyButtons.nextElementSibling;
      while (sibling) {
        const next = sibling.nextElementSibling;
        sibling.remove();
        sibling = next;
      }
    }

    const variantPicker = productGrid.querySelector('variant-picker');
    if (variantPicker && !this.#variantPickerHasChoices(variantPicker)) {
      variantPicker.remove();
    }
  }

  /**
   * @param {Element} variantPicker
   * @returns {boolean}
   */
  #variantPickerHasChoices(variantPicker) {
    for (const fieldset of variantPicker.querySelectorAll('fieldset.variant-option')) {
      const selects = fieldset.querySelectorAll('select.variant-option__select option:not([disabled])');
      if (selects.length > 1) return true;

      const radios = fieldset.querySelectorAll('input[type="radio"]:not([disabled])');
      if (radios.length > 1) return true;

      const buttons = fieldset.querySelectorAll(
        '.variant-option__button-label:not(.variant-option--hidden-by-liquid):not(.variant-option--filtered-out)'
      );
      if (buttons.length > 1) return true;
    }

    return false;
  }

  /**
   * Verwijder resterende PDP-ruis na morph (fallback naast CSS).
   * @param {Element} modalContent
   */
  #cleanupModalContent(modalContent) {
    const removeSelectors = [
      'product-button',
      'shopify-accelerated-checkout',
      '.accelerated-checkout-block',
      '.shopify-payment-button',
      'product-labels',
      '.product-labels',
      'product-inventory',
    ];

    for (const selector of removeSelectors) {
      modalContent.querySelectorAll(selector).forEach((el) => el.remove());
    }

    const detailsContent = modalContent.querySelector('.product-details .group-block-content');
    const buyButtons = detailsContent?.querySelector('.buy-buttons-block');
    if (buyButtons?.parentElement) {
      let sibling = buyButtons.nextElementSibling;
      while (sibling) {
        const next = sibling.nextElementSibling;
        sibling.remove();
        sibling = next;
      }
    }

    modalContent.querySelectorAll('.group-block').forEach((group) => {
      if (group.querySelector('.buy-buttons-block, product-price, variant-picker, .view-product-title')) return;
      if (!group.textContent?.replace(/\s+/g, '').trim()) group.remove();
    });
  }

  /**
   * @param {Document} html
   * @returns {Element | null}
   */
  #extractDescriptionSummary(html) {
    const summary =
      html.querySelector('.text-summary.text-summary--read-more-below') || html.querySelector('.text-summary');

    return summary ? /** @type {Element} */ (summary.cloneNode(true)) : null;
  }

  /**
   * @returns {Element | null}
   */
  #getCachedDescriptionSummary() {
    const url = this.productPageUrl;
    return url ? this.#cachedDescriptionSummary.get(url) || null : null;
  }

  /**
   * @param {Element} modalContent
   * @param {Element} element
   */
  #insertBeforeBuyButtons(modalContent, element) {
    const buyButtons = modalContent.querySelector('.buy-buttons-block');
    const parent = modalContent.querySelector('.product-details .group-block-content');

    if (parent && buyButtons) {
      parent.insertBefore(element, buyButtons);
      return;
    }

    const insertTarget =
      modalContent.querySelector('product-inventory') ||
      modalContent.querySelector('product-price') ||
      buyButtons;

    if (insertTarget?.parentElement) {
      insertTarget.insertAdjacentElement('afterend', element);
      return;
    }

    modalContent.appendChild(element);
  }

  /**
   * @param {Element} scope
   * @returns {Element | null}
   */
  #findDescriptionBlock(scope) {
    const buyButtons = scope.querySelector('.buy-buttons-block');

    for (const el of scope.querySelectorAll('.text-block.rte, .text-block.paragraph')) {
      if (buyButtons && !this.#isBefore(el, buyButtons)) continue;

      const text = el.textContent?.replace(/\s+/g, ' ').trim();
      if (text && text.length > 0) return el;
    }

    return null;
  }

  /**
   * @param {Element} a
   * @param {Element} b
   * @returns {boolean}
   */
  #isBefore(a, b) {
    return Boolean(a.compareDocumentPosition(b) & Node.DOCUMENT_POSITION_FOLLOWING);
  }

  /**
   * Voeg PDP text-summary toe (zelfde als "Over dit product" met Lees meer).
   * @param {Element} modalContent
   */
  #ensureDescription(modalContent) {
    modalContent.querySelector('.quick-add-modal__desc')?.remove();

    let hasDesc = false;
    const cachedSummary = this.#getCachedDescriptionSummary();

    if (cachedSummary) {
      const buyButtons = modalContent.querySelector('.buy-buttons-block');

      modalContent
        .querySelectorAll('.product-details .text-block.rte, .product-details .text-block.paragraph')
        .forEach((el) => {
          if (buyButtons && this.#isBefore(el, buyButtons) && !el.closest('.text-summary')) {
            el.remove();
          }
        });

      const summary = /** @type {Element} */ (cachedSummary.cloneNode(true));
      summary.classList.add('quick-add-modal__desc');

      const hasMobileDesc =
        isMobileBreakpoint() &&
        modalContent.querySelector(
          ':scope > .text-summary, :scope > .text-block.rte, :scope > .text-block.paragraph'
        );

      if (!hasMobileDesc) {
        this.#insertBeforeBuyButtons(modalContent, summary);
      }

      hasDesc = true;
    } else {
      const existingSummary = modalContent.querySelector('.product-details .text-summary');
      if (existingSummary) {
        existingSummary.classList.add('quick-add-modal__desc');
        hasDesc = true;
      } else if (this.#findDescriptionBlock(modalContent)) {
        hasDesc = true;
      }
    }

    modalContent.classList.toggle('quick-add-modal__content--has-desc', hasDesc);
  }

  /**
   * Syncs the variant selection from the product card to the modal
   * @param {Element} modalContent - The modal content element
   */
  #syncVariantSelection(modalContent) {
    const selectedVariantId = this.#getSelectedVariantId();
    if (!selectedVariantId) return;

    // Find and check the corresponding input in the modal
    const modalInputs = modalContent.querySelectorAll('input[type="radio"][data-variant-id]');
    for (const input of modalInputs) {
      if (input instanceof HTMLInputElement && input.dataset.variantId === selectedVariantId && !input.checked) {
        input.checked = true;
        input.dispatchEvent(new Event('change', { bubbles: true }));
        break;
      }
    }
  }
}

if (!customElements.get('quick-add-component')) {
  if (!customElements.get('quick-add-component')) customElements.define('quick-add-component', QuickAddComponent);
}

class QuickAddDialog extends DialogComponent {
  #abortController = new AbortController();

  connectedCallback() {
    super.connectedCallback();

    this.addEventListener(ThemeEvents.cartUpdate, this.handleCartUpdate, { signal: this.#abortController.signal });
    this.addEventListener(ThemeEvents.variantUpdate, this.#handleVariantUpdateInModal);

    this.addEventListener(DialogCloseEvent.eventName, this.#handleDialogClose);
  }

  disconnectedCallback() {
    super.disconnectedCallback();

    this.#abortController.abort();
    this.removeEventListener(DialogCloseEvent.eventName, this.#handleDialogClose);
  }

  /**
   * Closes the dialog
   * @param {CartUpdateEvent} event - The cart update event
   */
  handleCartUpdate = async (event) => {
    if (event.detail.data.didError) return;

    const fromQuickAddModal =
      event.target instanceof Element && Boolean(event.target.closest('#quick-add-dialog, .quick-add-modal'));

    if (fromQuickAddModal) {
      const vapQuickAdd = document.querySelector('variant-additional-products quick-add-component');
      if (vapQuickAdd instanceof QuickAddComponent) {
        vapQuickAdd.invalidateCacheForUrl(vapQuickAdd.productPageUrl);
      }
    }

    await this.closeDialog();
  };

  #handleVariantUpdateInModal = (/** @type {CustomEvent} */ event) => {
    if (!event.target || !(event.target instanceof Element) || !event.target.closest('.quick-add-modal')) {
      return;
    }

    this.#updateModalProductPrices(event);
    this.#updateProductTitleLink(event);

    // Variant updates in de modal mogen de PDP niet bereiken (VAP, teaser-text, enz.).
    event.stopPropagation();
  };

  /**
   * @param {CustomEvent} event
   */
  #updateModalProductPrices(event) {
    const html = event.detail?.data?.html;
    if (!(html instanceof Document || html instanceof DocumentFragment)) return;

    const source = event.target instanceof HTMLElement ? event.target : null;

    for (const priceEl of this.querySelectorAll('product-price')) {
      if (!(priceEl instanceof HTMLElement)) continue;

      if (
        source?.dataset.productId &&
        priceEl.dataset.productId &&
        source.dataset.productId !== priceEl.dataset.productId
      ) {
        continue;
      }

      const blockId = priceEl.dataset.blockId;
      if (!blockId) continue;

      const newProductPrice = html.querySelector(`product-price[data-block-id="${blockId}"]`);
      if (!newProductPrice) continue;

      const newPrice = newProductPrice.querySelector('[ref="priceContainer"]');
      const currentPrice = priceEl.querySelector('[ref="priceContainer"]');
      if (newPrice && currentPrice) currentPrice.replaceWith(/** @type {Element} */ (newPrice.cloneNode(true)));

      const currentNote = priceEl.querySelector('.volume-pricing-note');
      const newNote = newProductPrice.querySelector('.volume-pricing-note');

      if (!newNote) {
        currentNote?.remove();
      } else if (!currentNote) {
        priceEl
          .querySelector('[ref="priceContainer"]')
          ?.insertAdjacentElement('afterend', /** @type {Element} */ (newNote.cloneNode(true)));
      } else {
        currentNote.replaceWith(/** @type {Element} */ (newNote.cloneNode(true)));
      }
    }

    for (const unitPrice of this.querySelectorAll('pdp-unit-price')) {
      const fresh = html.querySelector('pdp-unit-price [data-pdp-unit-amount]');
      const current = unitPrice.querySelector('[data-pdp-unit-amount]');
      if (fresh && current) current.replaceWith(/** @type {Element} */ (fresh.cloneNode(true)));
    }
  }

  #updateProductTitleLink = (/** @type {CustomEvent} */ event) => {
    const anchorElement = /** @type {HTMLAnchorElement} */ (
      event.detail.data.html?.querySelector('.view-product-title a')
    );
    const viewMoreDetailsLink = /** @type {HTMLAnchorElement} */ (this.querySelector('.view-product-title a'));
    const mobileProductTitle = /** @type {HTMLAnchorElement} */ (this.querySelector('.product-header a'));

    if (!anchorElement) return;

    if (viewMoreDetailsLink) viewMoreDetailsLink.href = anchorElement.href;
    if (mobileProductTitle) mobileProductTitle.href = anchorElement.href;
  };

  #handleDialogClose = () => {
    const iosVersion = getIOSVersion();
    /**
     * This is a patch to solve an issue with the UI freezing when the dialog is closed.
     * To reproduce it, use iOS 16.0.
     */
    if (!iosVersion || iosVersion.major >= 17 || (iosVersion.major === 16 && iosVersion.minor >= 4)) return;

    requestAnimationFrame(() => {
      /** @type {HTMLElement | null} */
      const grid = document.querySelector('#ResultsList [product-grid-view]');
      if (grid) {
        const currentWidth = grid.getBoundingClientRect().width;
        grid.style.width = `${currentWidth - 1}px`;
        requestAnimationFrame(() => {
          grid.style.width = '';
        });
      }
    });
  };
}

if (!customElements.get('quick-add-dialog')) {
  if (!customElements.get('quick-add-dialog')) customElements.define('quick-add-dialog', QuickAddDialog);
}
